/**
 * Publisher Analytics – Engagement-Only Decision Surface (v1)
 * 
 * PURPOSE: Read-only engagement reporting as a decision surface for publishers.
 * Answers: "What should I do next?" based on actual reader engagement, not revenue.
 * 
 * METRICS (User-Facing):
 * - Reader Credits (RC) Consumed - from reader_credit_ledger
 * - Minutes Consumed - from playback data
 * - Ask Audia Questions - from audia_session_summaries
 * - Unique Readers - from reader_library
 * 
 * Route: /publisher/analytics
 * Mode: Read-only
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PublisherNav from '../components/PublisherNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  BarChart3,
  Users,
  BookOpen,
  UserCheck,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowLeft,
  Calendar,
  Activity,
  Clock,
  HelpCircle,
  Loader2,
  ChevronUp,
  ChevronDown,
  Coins,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

// ============================================================================
// FROZEN DEFINITIONS (Do not reinterpret)
// ============================================================================

const DEFINITIONS = {
  readerCredits: {
    term: "Reader Credits (RC)",
    definition: "Credits consumed by readers when engaging with your content"
  },
  minutesConsumed: {
    term: "Minutes Consumed",
    definition: "Total listening/reading time across all readers"
  },
  askAudia: {
    term: "Ask Audia Questions",
    definition: "Reader-initiated questions answered by the AI companion"
  },
  uniqueReaders: {
    term: "Unique Readers",
    definition: "Distinct readers who have engaged with your content"
  },
  activeTitles: {
    term: "Active Titles",
    definition: "Titles with reader engagement in the selected period"
  },
  activeAuthors: {
    term: "Active Authors",
    definition: "Authors with reader engagement in the selected period"
  }
};

// ============================================================================
// DATE FILTER OPTIONS
// ============================================================================

const DATE_FILTERS = [
  { value: 'today', label: 'Today', days: 0 },
  { value: '7days', label: 'Last 7 Days', days: 7 },
  { value: '30days', label: 'Last 30 Days', days: 30 },
  { value: 'year', label: 'Current Year', days: 365 },
  { value: 'lifetime', label: 'Lifetime', days: null },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatRC = (rc) => {
  if (!rc && rc !== 0) return '0.00';
  return parseFloat(rc).toFixed(2);
};

const formatMinutes = (seconds) => {
  if (!seconds || seconds === 0) return '0m';
  const mins = Math.floor(seconds / 60);
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  }
  return `${mins}m`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getDateRange = (filterValue) => {
  const now = new Date();
  let startDate;
  
  switch (filterValue) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30days':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    case 'lifetime':
    default:
      startDate = new Date('2020-01-01');
      break;
  }
  
  return {
    from: startDate.toISOString(),
    to: now.toISOString()
  };
};

const getTrendIcon = (trend) => {
  if (!trend || trend === 0) return <Minus className="w-4 h-4 text-slate-400" />;
  if (trend > 0) return <TrendingUp className="w-4 h-4 text-emerald-400" />;
  return <TrendingDown className="w-4 h-4 text-red-400" />;
};

const getTrendColor = (trend) => {
  if (!trend || trend === 0) return 'text-slate-400';
  if (trend > 0) return 'text-emerald-400';
  return 'text-red-400';
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Definition Tooltip
const DefinitionTooltip = ({ term, children }) => {
  const def = Object.values(DEFINITIONS).find(d => d.term.toLowerCase().includes(term.toLowerCase()));
  if (!def) return children;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help border-b border-dashed border-slate-500">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-800 border-slate-700 max-w-xs">
          <p className="text-sm font-medium text-white">{def.term}</p>
          <p className="text-xs text-slate-400 mt-1">{def.definition}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Engagement Disclaimer Banner
const EngagementDisclaimer = () => (
  <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3 mb-6" data-testid="engagement-disclaimer">
    <div className="flex items-start gap-2">
      <Activity className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-blue-300 font-medium">
          Engagement Analytics (Read-Only)
        </p>
        <p className="text-xs text-blue-400/80 mt-0.5">
          Metrics represent reader engagement signals only. This is not a financial or payout dashboard.
        </p>
      </div>
    </div>
  </div>
);

// KPI Card
const KPICard = ({ icon: Icon, label, value, subValue, trend, color = "blue", tooltip }) => {
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    emerald: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  };
  
  return (
    <Card className={`${colorClasses[color]} border`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium opacity-80">
              {tooltip ? <DefinitionTooltip term={tooltip}>{label}</DefinitionTooltip> : label}
            </span>
          </div>
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center gap-1 text-xs ${getTrendColor(trend)}`}>
              {getTrendIcon(trend)}
              <span>{trend > 0 ? '+' : ''}{trend}%</span>
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-white mt-2">{value}</p>
        {subValue && <p className="text-xs opacity-60 mt-1">{subValue}</p>}
      </CardContent>
    </Card>
  );
};

// Sortable Table Header
const SortableHeader = ({ label, sortKey, currentSort, onSort, tooltip }) => {
  const isActive = currentSort.key === sortKey;
  const direction = isActive ? currentSort.direction : null;
  
  return (
    <TableHead 
      className="text-slate-400 cursor-pointer hover:text-slate-200 select-none"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {tooltip ? <DefinitionTooltip term={tooltip}>{label}</DefinitionTooltip> : label}
        <div className="flex flex-col">
          <ChevronUp className={`w-3 h-3 -mb-1 ${isActive && direction === 'asc' ? 'text-blue-400' : 'text-slate-600'}`} />
          <ChevronDown className={`w-3 h-3 -mt-1 ${isActive && direction === 'desc' ? 'text-blue-400' : 'text-slate-600'}`} />
        </div>
      </div>
    </TableHead>
  );
};

// ============================================================================
// AUTHOR DETAIL MODAL
// ============================================================================

const AuthorDetailModal = ({ author, isOpen, onClose, dateRange, onTitleClick, allTitles }) => {
  const [titleSort, setTitleSort] = useState({ key: 'rcConsumed', direction: 'desc' });

  // Filter titles for this author
  const authorTitles = allTitles.filter(t => t.authorId === author?.id);

  const sortTitles = (key) => {
    setTitleSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedTitles = [...authorTitles].sort((a, b) => {
    const aVal = a[titleSort.key] || 0;
    const bVal = b[titleSort.key] || 0;
    return titleSort.direction === 'desc' ? bVal - aVal : aVal - bVal;
  });

  // Calculate author totals from titles
  const totalRC = authorTitles.reduce((sum, t) => sum + (t.rcConsumed || 0), 0);
  const totalMinutes = authorTitles.reduce((sum, t) => sum + (t.minutesConsumed || 0), 0);
  const totalAskAudia = authorTitles.reduce((sum, t) => sum + (t.askAudiaQuestions || 0), 0);

  if (!author) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-purple-400" />
            {author.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Author Engagement Detail (Read-Only)
          </DialogDescription>
        </DialogHeader>

        {/* Author Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="reader credits">RC Consumed</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatRC(totalRC)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="minutes">Minutes</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatMinutes(totalMinutes * 60)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="ask audia">Ask Audia</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatNumber(totalAskAudia)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Titles</p>
            <p className="text-xl font-bold text-white">{authorTitles.length}</p>
          </div>
        </div>

        {/* Author Titles Table */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Titles by This Author</h3>
          {authorTitles.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No titles found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Title</TableHead>
                  <SortableHeader label="RC Consumed" sortKey="rcConsumed" currentSort={titleSort} onSort={sortTitles} tooltip="reader credits" />
                  <SortableHeader label="Minutes" sortKey="minutesConsumed" currentSort={titleSort} onSort={sortTitles} tooltip="minutes" />
                  <SortableHeader label="Ask Audia" sortKey="askAudiaQuestions" currentSort={titleSort} onSort={sortTitles} tooltip="ask audia" />
                  <SortableHeader label="Readers" sortKey="uniqueReaders" currentSort={titleSort} onSort={sortTitles} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTitles.map((title) => (
                  <TableRow 
                    key={title.id} 
                    className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => onTitleClick(title)}
                  >
                    <TableCell className="text-white font-medium">{title.name}</TableCell>
                    <TableCell className="text-slate-300">{formatRC(title.rcConsumed)}</TableCell>
                    <TableCell className="text-slate-300">{formatMinutes(title.minutesConsumed * 60)}</TableCell>
                    <TableCell className="text-slate-300">{formatNumber(title.askAudiaQuestions)}</TableCell>
                    <TableCell className="text-slate-300">{formatNumber(title.uniqueReaders)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-700 mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// TITLE DETAIL MODAL
// ============================================================================

const TitleDetailModal = ({ title, isOpen, onClose }) => {
  if (!title) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-400" />
            {title.name}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            by {title.authorName} • Title Engagement Detail (Read-Only)
          </DialogDescription>
        </DialogHeader>

        {/* Title Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="reader credits">RC Consumed</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatRC(title.rcConsumed)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="minutes">Minutes</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatMinutes(title.minutesConsumed * 60)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="ask audia">Ask Audia</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatNumber(title.askAudiaQuestions)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">
              <DefinitionTooltip term="unique readers">Unique Readers</DefinitionTooltip>
            </p>
            <p className="text-xl font-bold text-white">{formatNumber(title.uniqueReaders)}</p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-800/30 rounded-lg p-3">
            <p className="text-xs text-slate-500">Audio Status</p>
            <p className="text-sm font-medium text-white capitalize">{title.audioStatus || 'N/A'}</p>
          </div>
          <div className="bg-slate-800/30 rounded-lg p-3">
            <p className="text-xs text-slate-500">Created</p>
            <p className="text-sm font-medium text-white">{formatDate(title.createdAt)}</p>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-700 mt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PublisherAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState(searchParams.get('period') || '30days');
  const [dateRange, setDateRange] = useState(getDateRange('30days'));
  
  // Data
  const [kpiData, setKpiData] = useState({
    totalRC: 0,
    totalMinutes: 0,
    totalAskAudia: 0,
    uniqueReaders: 0,
    activeTitles: 0,
    activeAuthors: 0
  });
  const [authors, setAuthors] = useState([]);
  const [titles, setTitles] = useState([]);
  
  // Sorting
  const [authorSort, setAuthorSort] = useState({ key: 'rcConsumed', direction: 'desc' });
  const [titleSort, setTitleSort] = useState({ key: 'rcConsumed', direction: 'desc' });
  
  // Modals
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);

  // -------------------------------------------------------------------------
  // EFFECTS
  // -------------------------------------------------------------------------

  useEffect(() => {
    const range = getDateRange(dateFilter);
    setDateRange(range);
    setSearchParams({ period: dateFilter });
  }, [dateFilter]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  // -------------------------------------------------------------------------
  // DATA FETCHING
  // -------------------------------------------------------------------------

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = token || localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${authToken}` };

      // Fetch authors
      const authorsResponse = await axios.get(`${API}/publisher/authors`, { headers });

      // Fetch titles with engagement data (includes total_reader_credits, unique_readers)
      const titlesResponse = await axios.get(`${API}/publisher/titles`, { headers });

      // Process authors data
      const authorsMap = {};
      (authorsResponse.data.authors || []).forEach(author => {
        authorsMap[author.id] = {
          id: author.id,
          name: author.pen_name || 'Unknown Author',
          titleCount: author.title_count || 0,
          rcConsumed: 0,
          minutesConsumed: 0,
          askAudiaQuestions: 0,
          uniqueReaders: 0,
          lastActivity: author.updated_at || author.created_at
        };
      });

      // Process titles data and aggregate to authors
      const titlesData = (titlesResponse.data.titles || []).map(title => {
        const authorId = title.publisher_author_id;
        const rcConsumed = Math.abs(title.total_reader_credits || 0);
        const uniqueReaders = title.unique_readers || 0;
        
        // Estimate minutes from RC (approx 1 RC = 10 mins of engagement)
        const minutesConsumed = rcConsumed * 10;
        
        // Ask Audia questions - from audia_session_summaries via backend
        const askAudiaQuestions = title.total_audia_questions || 0;

        // Aggregate to author
        if (authorsMap[authorId]) {
          authorsMap[authorId].rcConsumed += rcConsumed;
          authorsMap[authorId].minutesConsumed += minutesConsumed;
          authorsMap[authorId].askAudiaQuestions += askAudiaQuestions;
          authorsMap[authorId].uniqueReaders += uniqueReaders;
        }

        return {
          id: title.id || title.manuscript_id,
          manuscriptId: title.manuscript_id,
          name: title.title || title.title_name || 'Untitled',
          authorName: title.author_display_name || 'Unknown Author',
          authorId: authorId,
          rcConsumed,
          minutesConsumed,
          askAudiaQuestions,
          uniqueReaders,
          audioStatus: title.audio_status,
          createdAt: title.created_at,
          lastActivity: title.updated_at
        };
      });

      const authorsData = Object.values(authorsMap);
      
      setAuthors(authorsData);
      setTitles(titlesData);

      // Calculate KPIs
      const totalRC = titlesData.reduce((sum, t) => sum + t.rcConsumed, 0);
      const totalMinutes = titlesData.reduce((sum, t) => sum + t.minutesConsumed, 0);
      const totalAskAudia = titlesData.reduce((sum, t) => sum + t.askAudiaQuestions, 0);
      const totalUniqueReaders = titlesResponse.data.total_unique_readers || 
        titlesData.reduce((sum, t) => sum + t.uniqueReaders, 0);
      const activeTitles = titlesData.filter(t => t.rcConsumed > 0).length;
      const activeAuthors = authorsData.filter(a => a.rcConsumed > 0).length;
      
      setKpiData({
        totalRC,
        totalMinutes,
        totalAskAudia,
        uniqueReaders: totalUniqueReaders,
        activeTitles,
        activeAuthors
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.status === 403) {
        toast.error('Publisher role required to view analytics');
      } else if (error.response?.status === 404) {
        toast.error('Publisher profile not found. Please set up your publisher profile first.');
      } else {
        toast.error('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange, token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalyticsData();
  };

  // -------------------------------------------------------------------------
  // SORTING
  // -------------------------------------------------------------------------

  const sortAuthors = (key) => {
    setAuthorSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortTitles = (key) => {
    setTitleSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedAuthors = [...authors].sort((a, b) => {
    let aVal = a[authorSort.key];
    let bVal = b[authorSort.key];
    if (authorSort.key === 'lastActivity') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    return authorSort.direction === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const sortedTitles = [...titles].sort((a, b) => {
    let aVal = a[titleSort.key];
    let bVal = b[titleSort.key];
    if (titleSort.key === 'lastActivity') {
      aVal = new Date(aVal || 0).getTime();
      bVal = new Date(bVal || 0).getTime();
    }
    return titleSort.direction === 'desc' ? bVal - aVal : aVal - bVal;
  });

  // -------------------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------------------

  const handleAuthorClick = (author) => {
    setSelectedAuthor(author);
    setShowAuthorModal(true);
  };

  const handleTitleClick = (title) => {
    setSelectedTitle(title);
    setShowTitleModal(true);
  };

  const handleTitleFromAuthor = (title) => {
    setShowAuthorModal(false);
    setTimeout(() => {
      setSelectedTitle(title);
      setShowTitleModal(true);
    }, 100);
  };

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50" data-testid="publisher-analytics-page">
      <PublisherNav />
      
      <div className="pb-20 md:pb-0">
        <div className="mx-auto max-w-7xl px-4 py-6">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/publisher')}
                className="text-slate-400 hover:text-white mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-blue-400" />
                Publisher Analytics
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Engagement reporting • What should I do next?
              </p>
            </div>
            
            {/* Global Date Filter */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Calendar className="w-4 h-4" />
                <span>Period:</span>
              </div>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white" data-testid="date-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {DATE_FILTERS.map(f => (
                    <SelectItem key={f.value} value={f.value} className="text-white hover:bg-slate-700">
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-700"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          <EngagementDisclaimer />

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* KPI Summary */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="kpi-summary">
                <KPICard
                  icon={Coins}
                  label="RC Consumed"
                  value={formatRC(kpiData.totalRC)}
                  tooltip="reader credits"
                  color="purple"
                />
                <KPICard
                  icon={Clock}
                  label="Minutes"
                  value={formatMinutes(kpiData.totalMinutes * 60)}
                  tooltip="minutes"
                  color="blue"
                />
                <KPICard
                  icon={MessageSquare}
                  label="Ask Audia"
                  value={formatNumber(kpiData.totalAskAudia)}
                  tooltip="ask audia"
                  color="emerald"
                />
                <KPICard
                  icon={Users}
                  label="Readers"
                  value={formatNumber(kpiData.uniqueReaders)}
                  tooltip="unique readers"
                  color="amber"
                />
                <KPICard
                  icon={BookOpen}
                  label="Active Titles"
                  value={formatNumber(kpiData.activeTitles)}
                  tooltip="active titles"
                  color="blue"
                />
                <KPICard
                  icon={UserCheck}
                  label="Active Authors"
                  value={formatNumber(kpiData.activeAuthors)}
                  tooltip="active authors"
                  color="purple"
                />
              </div>

              {/* Performance Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Authors Table */}
                <Card className="bg-slate-900/60 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Authors – Performance Summary</CardTitle>
                    <CardDescription>Click any row to drill down</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {authors.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No authors found</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700">
                              <TableHead className="text-slate-400">Author</TableHead>
                              <SortableHeader label="RC Consumed" sortKey="rcConsumed" currentSort={authorSort} onSort={sortAuthors} />
                              <SortableHeader label="Minutes" sortKey="minutesConsumed" currentSort={authorSort} onSort={sortAuthors} />
                              <SortableHeader label="Ask Audia" sortKey="askAudiaQuestions" currentSort={authorSort} onSort={sortAuthors} />
                              <SortableHeader label="Titles" sortKey="titleCount" currentSort={authorSort} onSort={sortAuthors} />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedAuthors.slice(0, 10).map((author) => (
                              <TableRow 
                                key={author.id} 
                                className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                                onClick={() => handleAuthorClick(author)}
                                data-testid={`author-row-${author.id}`}
                              >
                                <TableCell className="text-white font-medium">{author.name}</TableCell>
                                <TableCell className="text-slate-300">{formatRC(author.rcConsumed)}</TableCell>
                                <TableCell className="text-slate-300">{formatMinutes(author.minutesConsumed * 60)}</TableCell>
                                <TableCell className="text-slate-300">{formatNumber(author.askAudiaQuestions)}</TableCell>
                                <TableCell className="text-slate-300">{author.titleCount}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Titles Table */}
                <Card className="bg-slate-900/60 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Titles – Performance Summary</CardTitle>
                    <CardDescription>Sorted by RC Consumed by default</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {titles.length === 0 ? (
                      <p className="text-slate-500 text-center py-8">No titles found</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700">
                              <TableHead className="text-slate-400">Title</TableHead>
                              <TableHead className="text-slate-400">Author</TableHead>
                              <SortableHeader label="RC" sortKey="rcConsumed" currentSort={titleSort} onSort={sortTitles} />
                              <SortableHeader label="Minutes" sortKey="minutesConsumed" currentSort={titleSort} onSort={sortTitles} />
                              <SortableHeader label="Readers" sortKey="uniqueReaders" currentSort={titleSort} onSort={sortTitles} />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedTitles.slice(0, 10).map((title) => (
                              <TableRow 
                                key={title.id} 
                                className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                                onClick={() => handleTitleClick(title)}
                                data-testid={`title-row-${title.id}`}
                              >
                                <TableCell className="text-white font-medium max-w-[150px] truncate">{title.name}</TableCell>
                                <TableCell className="text-slate-400">{title.authorName}</TableCell>
                                <TableCell className="text-slate-300">{formatRC(title.rcConsumed)}</TableCell>
                                <TableCell className="text-slate-300">{formatMinutes(title.minutesConsumed * 60)}</TableCell>
                                <TableCell className="text-slate-300">{formatNumber(title.uniqueReaders)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>

              </div>

              {/* Definitions Reference */}
              <Card className="bg-slate-900/40 border-slate-800 mt-8">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Metric Definitions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                    {Object.values(DEFINITIONS).map(def => (
                      <div key={def.term}>
                        <p className="font-medium text-slate-300">{def.term}</p>
                        <p className="text-slate-500">{def.definition}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <AuthorDetailModal
        author={selectedAuthor}
        isOpen={showAuthorModal}
        onClose={() => setShowAuthorModal(false)}
        dateRange={dateRange}
        onTitleClick={handleTitleFromAuthor}
        allTitles={titles}
      />

      <TitleDetailModal
        title={selectedTitle}
        isOpen={showTitleModal}
        onClose={() => setShowTitleModal(false)}
      />

      <UniversalFooter />
    </div>
  );
};

export default PublisherAnalyticsPage;
